'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import SplitReveal from '@/components/motion/SplitReveal';
import Eyebrow from '@/components/ui/Eyebrow';

const faqItems = [
  {
    title: 'How to copy and paste components in Lensync?',
    content:
      'Lensync provides an intuitive interface for copying and pasting components. Simply select the component you wish to copy, use the right-click menu or keyboard shortcut (Ctrl+C), then paste it where needed (Ctrl+V). All properties and configurations will be preserved.',
  },
  {
    title: 'How frequently will you update the Components?',
    content:
      'Barcoding integration optimizes decision-making, freeing employees to prioritize customer service and production over data retrieval and reordering. Edging parameters are automatically set during order creation, boosting efficiency.',
  },
  {
    title: 'Why do I need Lensync?',
    content:
      'Lensync streamlines your optical business operations by integrating all aspects of lab management, order entry, and practice management into one cohesive platform. This integration eliminates redundant data entry, reduces errors, and allows your team to focus on customer service rather than administrative tasks.',
  },
  {
    title: 'What are Components?',
    content:
      'Components in Lensync are modular building blocks that make up the system. They include features like the Remote Order Entry, Lab Management System, Practice Management System, and Edging Module. Each component can be used independently or as part of the complete solution, allowing for a customized implementation based on your specific business needs.',
  },
];

const reveal = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

const FaqSection = () => {
  const [selected, setSelected] = useState(0);

  return (
    <section className="relative overflow-hidden py-24 sm:py-32">
      {/* ambient glow — brand blue + mint, no purple */}
      <div className="pointer-events-none absolute -left-24 top-16 size-72 rounded-full bg-primary-blue/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-8 size-72 rounded-full bg-emerald-200/30 blur-3xl" />

      <div className="container relative mx-auto px-5">
        {/* intro */}
        <div className="mx-auto max-w-4xl text-center">
          <Eyebrow className="mb-5">Frequently Asked Questions</Eyebrow>
          <SplitReveal className="display text-[clamp(34px,4.4vw,64px)] text-secondary-dark">
            Find Answers to Your Questions
          </SplitReveal>
        </div>

        {/* two-column FAQ: questions left, answer in a fixed panel on the right */}
        <div className="mx-auto mt-14 grid max-w-6xl gap-5 lg:grid-cols-[0.92fr_1.08fr] lg:items-start lg:gap-8">
          {/* questions */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            transition={{ staggerChildren: 0.07 }}
            className="space-y-3 lg:flex lg:h-[300px] lg:flex-col lg:gap-3 lg:space-y-0"
          >
            {faqItems.map((item, idx) => {
              const active = selected === idx;
              return (
                <motion.button
                  key={item.title}
                  type="button"
                  variants={reveal}
                  onClick={() => setSelected(idx)}
                  aria-pressed={active}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl border px-5 py-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-blue focus-visible:ring-offset-2 lg:flex-1 ${
                    active
                      ? 'border-ink bg-ink'
                      : 'border-secondary-dark/[0.07] bg-white hover:border-secondary-dark/20'
                  }`}
                >
                  <span
                    className={`font-semibold tracking-tight ${active ? 'text-white' : 'text-secondary-dark'}`}
                  >
                    {item.title}
                  </span>
                  <ChevronRight
                    className={`size-5 shrink-0 transition-transform ${
                      active ? 'translate-x-0.5 text-primary-blue' : 'text-secondary-dark/30'
                    }`}
                    strokeWidth={2}
                  />
                </motion.button>
              );
            })}
          </motion.div>

          {/* answer panel — fixed height so the page never grows or jumps */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
            className="h-[340px] overflow-y-auto rounded-2xl border border-secondary-dark/[0.07] bg-white p-7 shadow-[0_30px_80px_-30px_rgba(2,32,71,0.25)] sm:h-[300px] sm:p-9 lg:sticky lg:top-24"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={selected}
                initial={{ opacity: 0, x: 14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -14 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="label text-[10px] text-primary-blue">Answer</span>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-secondary-dark">
                  {faqItems[selected].title}
                </h3>
                <p className="mt-3 leading-relaxed text-gray-500">{faqItems[selected].content}</p>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
